import L from "leaflet";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, Marker, TileLayer, useMap } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import FilterPanel from "./FilterPanel";
import LayerDropdown from "./LayerDropdown";
import { message } from "antd";
import "leaflet-control-geocoder";
import "leaflet-control-geocoder/dist/Control.Geocoder.css";
import "leaflet/dist/leaflet.css";
import { debounce } from "lodash";
import { useNavigate, useLocation } from "react-router-dom";
import { BaseURL } from "../../Utils/baseURL";
import CompensatoryTreeIcon from "../../images/CompensatoryTreeIcon.svg";
import { ReactComponent as FilterIcon } from "../../images/FilterIcon.svg";
import { ReactComponent as FilterIcon2 } from "../../images/FilterIcon2.svg";
import In_situTreeIcon from "../../images/In_situTreeIcon.svg";
import { ReactComponent as LayerIcon } from "../../images/MapLayersIcon.svg";
import { ReactComponent as LayerIcon2 } from "../../images/MapLayersIcon2.svg";
import { ReactComponent as RulerIcon } from "../../images/RulerIcon.svg";
import TransplanTreeIcon from "../../images/TransplanTreeIcon.svg";
import MetrostationIcon from "../../images/MetrostationIcon.svg";
import MetroIcon from "../../images/MetroIcon.svg";
import { treeIcon } from "../Icons";
import Legend from "./Legend";
import "./MapComponent.css";
import SearchBox from "./SearchBox";
import TreeInfoModal from "./TreeInfoModal";
import Loading from "../../Utils/Loader";
import { ReactComponent as DashboardIcon } from "../../images/DashboardIcon.svg";
import { ReactComponent as InputformIcon } from "../../images/InputformIcon.svg";
import LocationMarker from "../../images/LocationMarker.svg";
import Logo2 from "../../images/Logo.svg";
import { ReactComponent as UserAccountIcon } from "../../images/UserAccountIcon.svg";
import UserAccount from "../Auth/UserAccount";
import { fetchTreeData } from "../GISPortel/data";

const MapComponent = () => {
  const [filters, setFilters] = useState({});
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const filterPanelRef = useRef(null);
  const [user, setUser] = useState({});
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const position = useMemo(() => [19.116084601593794, 72.86542060979293], []); // Memoized position
  const [showLayers, setShowLayers] = useState(false);
  const [isTreeOn, setIsTreeOn] = useState(true);
  const [selectedLayer, setSelectedLayer] = useState("Satellite");
  const [isFilterOpen, setFilterOpen] = useState(false);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [markerPosition, setMarkerPosition] = useState([18.9326785, 72.82506531582413]);
  const [metroLineLayer, setMetroLineLayer] = useState(null);
  const [isDataFetched, setIsDataFetched] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [open, setOpen] = useState(false);
  const [disableMapInteraction, setDisableMapInteraction] = useState(false);
  const [trees, setTrees] = useState([]);
  const [dataVisible, setDataVisible] = useState(false);
  const [selectedTree, setSelectedTree] = useState(null);
  const [showTrees, setShowTrees] = useState(true);
  const [treeData, setTreeData] = useState([]);
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [filteredTrees, setFilteredTrees] = useState(trees);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [currentSidebarView, setCurrentSidebarView] = useState("tree");
  const [selectedTreeData, setSelectedTreeData] = useState(null);
  const [sidebarView, setSidebarView] = useState("filter");
  const [selectedTreeName, setSelectedTreeName] = useState([]);
  const [selectedStation, setSelectedStation] = useState(null);
  const [selectedSurveyTypes, setSelectedSurveyTypes] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [highlightedLocation, setHighlightedLocation] = useState(null);
  const [mapInstance, setMapInstance] = useState(null);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState([]);
  const [expandedTreeFilter, setExpandedTreeFilter] = useState(false);
  const [expandedLocationFilter, setExpandedLocationFilter] = useState(false);
  const [zoom, setZoom] = useState(11.5);
  const [searchTree, setSearchTree] = useState("");
  const [searchLocation, setSearchLocation] = useState("");
  const [showMoreTree, setShowMoreTree] = useState(false);
  const [showMoreLocation, setShowMoreLocation] = useState(false);
  const [filterApplied, setFilterApplied] = useState(false);
  const surveyType = ["in_situ", "transplant", "compensatory"];
  const defaultColors = ["#B6F092", "#FFA1E3", "#FA9658"];
  const [selectedMap, setSelectedMap] = useState(null);
  const debouncedSearchTree = useMemo(() => debounce((value) => setSearchTree(value), 300), []);
  const debouncedSearchLocation = useMemo(() => debounce((value) => setSearchLocation(value), 300), []);
  const [stationsLayer, setStationsLayer] = useState(null);
  const [metroLayer, setMetroLayer] = useState(null);
  const [selectedFilters, setSelectedFilters] = useState({
    treeName: null,
    location: null,
    surveyType: null,
  });
  const [activeButton, setActiveButton] = useState(null);

  const indiaBounds = useMemo(() => L.latLngBounds([6.5546079, 68.1113787], [35.675147, 97.395561]), []);
  const location = useLocation();

  // Memoized handlers
  const handleApplyFilters = useCallback((filteredData) => {
    setFilteredTrees(filteredData);
    if (filteredData.length > 0) focusOnLocation(filteredData);
    setFilterApplied(true);
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilteredTrees(trees);
    if (mapRef.current) mapRef.current.flyTo(position, 11.5, { duration: 1.2 });
  }, [trees, position]);

  useEffect(() => {
    const loadTrees = async () => {
      setLoading(true);
      const data = await fetchTreeData();
      setTrees(data);
      setFilteredTrees(data);
      setLoading(false);
    };
    loadTrees();
  }, []);

  useEffect(() => {
    if (location.state?.updatedTree) {
      const updatedTree = location.state.updatedTree;
      setTrees((prevTrees) =>
        prevTrees.map((tree) => (tree.properties.tid === updatedTree.properties.tid ? updatedTree : tree))
      );
      setFilteredTrees((prevFiltered) =>
        prevFiltered.map((tree) => (tree.properties.tid === updatedTree.properties.tid ? updatedTree : tree))
      );
      setSelectedTree(updatedTree);
      setIsModalVisible(true);
    }
  }, [location.state]);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => setCurrentLocation({ lat: position.coords.latitude, lng: position.coords.longitude }),
        (error) => console.error("Error getting geolocation: ", error)
      );
    }
  }, []);

  useEffect(() => {
    if (currentLocation && mapRef.current) {
      if (markerRef.current) markerRef.current.setLatLng([currentLocation.lat, currentLocation.lng]);
    }
  }, [currentLocation]);

  useEffect(() => {
    if (mapRef.current) {
      const handleMapClick = () => {
        if (markerRef.current) {
          mapRef.current.removeLayer(markerRef.current);
          markerRef.current = null;
        }
      };
      mapRef.current.on("click", handleMapClick);
      return () => mapRef.current.off("click", handleMapClick);
    }
  }, []);

  useEffect(() => {
    const loadMetroLineData = async () => {
      try {
        const metroResponse = await fetch("/MML3_Alignment.geojson");
        const metroData = await metroResponse.json();
        const metroLineStyle = { color: "#02b7bb", weight: 4, opacity: 1, dashArray: "5,5", dashOffset: 0 };
        const metroLayer = L.geoJSON(metroData, { style: metroLineStyle });

        if (mapRef.current) {
          metroLayer.addTo(mapRef.current);
          setMetroLayer(metroLayer);
          const interval = setInterval(() => {
            metroLineStyle.dashOffset = (metroLineStyle.dashOffset + 1) % 20;
            metroLayer.setStyle(metroLineStyle);
          }, 100);
          return () => clearInterval(interval); // Cleanup
        }
      } catch (error) {
        console.error("Error loading Metro Line data:", error);
      }
    };

    const loadStationsData = async () => {
      try {
        const stationsResponse = await fetch("/MML3_Stations.geojson");
        const stationsData = await stationsResponse.json();
        const stationIcon = L.divIcon({
          className: "station-icon",
          html: `<div style="display: flex; justify-content: center; align-items: center;"><img src="${MetrostationIcon}" alt="Station Icon" style="width: 20px; height: 20px;" /></div>`,
          iconSize: [20, 20],
          iconAnchor: [10, 10],
        });
        const stationsLayer = L.geoJSON(stationsData, {
          pointToLayer: (feature, latlng) => {
            const marker = L.marker(latlng, { icon: stationIcon });
            marker.bindPopup(
              `<div style="display: flex; font-family: 'Poppins-Semi', sans-serif; font-size: 14px;">
                <img src="${MetroIcon}" alt="Station Icon" style="width: 20px; height: 20px; margin-right: 8px;"/>
                <b>Station Name : </b> ${feature.properties.Name || "N/A"}
              </div>`
            );
            return marker;
          },
        });

        if (mapRef.current) {
          stationsLayer.addTo(mapRef.current);
          setStationsLayer(stationsLayer);
        }
      } catch (error) {
        console.error("Error loading Stations data:", error);
      }
    };

    loadMetroLineData();
    loadStationsData();

    return () => {
      if (mapRef.current) {
        if (metroLayer) mapRef.current.removeLayer(metroLayer);
        if (stationsLayer) mapRef.current.removeLayer(stationsLayer);
      }
    };
  }, []); // Empty dependency array to run once

  const handleFilterToggle = useCallback(() => {
    setActiveButton((prev) => (prev === "filter" ? null : "filter"));
    setShowFilterPanel((prev) => !prev);
  }, []);

  const handleFlyToCurrentLocation = useCallback(() => {
    setActiveButton((prev) => (prev === "currentLoc" ? null : "currentLoc"));
    if (currentLocation && mapRef.current) {
      mapRef.current.flyTo([currentLocation.lat, currentLocation.lng], 13, { duration: 0.9 });
      const locationIcon = L.icon({ iconUrl: LocationMarker, iconSize: [40, 40], iconAnchor: [20, 20] });
      if (!markerRef.current) {
        markerRef.current = L.marker([currentLocation.lat, currentLocation.lng], { icon: locationIcon }).addTo(mapRef.current);
      } else {
        markerRef.current.setLatLng([currentLocation.lat, currentLocation.lng]);
      }
    }
  }, [currentLocation]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (filterPanelRef.current && !filterPanelRef.current.contains(event.target) && showFilterPanel) {
        setShowFilterPanel(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [showFilterPanel]);

  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (token) {
      setIsLoggedIn(true);
      fetchUserData(token);
    }
  }, []);

  const fetchUserData = async (token) => {
    try {
      const response = await fetch(`${BaseURL}/auth/users/details/`, {
        method: "GET",
        headers: { "Authorization": `token ${token}`, "Content-Type": "application/json" },
      });
      if (response.ok) setUser(await response.json().data);
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };

  const handleLogin = (userData, token) => {
    setIsLoggedIn(true);
    setUser(userData);
    sessionStorage.setItem("token", token);
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setUser(null);
    sessionStorage.removeItem("token");
    message.success("Logout successful!");
    setIsAccountOpen(false);
    navigate("/");
  };

  const toggleAccount = () => setIsAccountOpen((prev) => !prev);

  const activeButtonStyle = (buttonName) => ({
    ...buttonStyle,
    backgroundColor: activeButton === buttonName ? "transparent" : "transparent", // Highlight active button
  });

  const handleLayerToggle = useCallback(() => {
    setDropdownOpen((prev) => !prev);
    setActiveButton((prev) => (prev === "layer" ? null : "layer"));
  }, []);

  const createCustomClusterIcon = useCallback((cluster) => {
    const markers = cluster.getAllChildMarkers();
    const surveyTypeCounts = markers.reduce((acc, marker) => {
      const type = marker.options.surveyType;
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {});
    const mostCommonType = Object.keys(surveyTypeCounts).reduce((a, b) =>
      surveyTypeCounts[a] > surveyTypeCounts[b] ? a : b
    );
    const bgColor =
      mostCommonType === "in_situ" ? "#B6F092" : mostCommonType === "transplant" ? "#FFA1E3" : "#FA9658";
    const clusterCount = cluster.getChildCount();
    const clusterIcon = getIconBySurveyType(mostCommonType).options.iconUrl;

    return L.divIcon({
      html: `
        <div class="enhanced-cluster-icon" style="background-color: ${bgColor};">
          <img src="${clusterIcon}" alt="${mostCommonType}" />
          <div class="cluster-count">${clusterCount}</div>
        </div>
      `,
      className: "enhanced-cluster-wrapper",
      iconSize: [50, 50],
    });
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showLayers && !event.target.closest(".layers-dropdown")) setShowLayers(true);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showLayers]);

  const getIconBySurveyType = useCallback((surveyType) => {
    switch (surveyType) {
      case "in_situ":
        return new L.icon({ iconUrl: In_situTreeIcon, iconSize: [25, 41], iconAnchor: [12, 41] });
      case "transplant":
        return new L.icon({ iconUrl: TransplanTreeIcon, iconSize: [25, 41], iconAnchor: [12, 41] });
      case "compensatory":
        return new L.icon({ iconUrl: CompensatoryTreeIcon, iconSize: [25, 41], iconAnchor: [12, 41] });
      default:
        return new L.icon({ iconUrl: treeIcon, iconSize: [25, 41], iconAnchor: [12, 41] });
    }
  }, []);

  const focusOnLocation = useCallback((filteredData) => {
    if (mapRef.current && filteredData.length > 0) {
      const bounds = L.latLngBounds(
        filteredData.map((tree) => [tree.geometry.coordinates[1], tree.geometry.coordinates[0]])
      );
      mapRef.current.flyToBounds(bounds, { padding: [50, 50], duration: 1.5 });
    }
  }, []);

  const handleMarkerClick = useCallback((tree) => {
    setSelectedTree(tree);
    setIsModalVisible(true);
  }, []);

  const handleModalClose = () => {
    setIsModalVisible(false);
    setSelectedTree(null);
  };

  const handleRedirectToInputForm = () => navigate("/inputform");

  const buttonStyle = useMemo(
    () => ({
      background: "transparent",
      height: "40px",
      width: "40px",
      border: "none",
      cursor: "pointer",
      padding: "5px",
      margin: "5px",
    }),
    []
  );

  const handleLayerChange = useCallback((layer) => {
    setSelectedLayer(layer);
    setSelectedMap(layer);
    setShowLayers(false);
  }, []);

  const ZoomControl = () => {
    const map = useMap();
    const handleZoomIn = () => {
      map.zoomIn();
      setZoom(map.getZoom());
    };
    const handleZoomOut = () => {
      map.zoomOut();
      setZoom(map.getZoom());
    };
    return (
      <div
        style={{
          marginTop: "205px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "10px 0",
          marginBottom: "5px",
          border: "none",
          borderRadius: "5px",
          cursor: "pointer",
          width: "39px",
          height: "auto",
          marginLeft: "30px",
          backgroundColor: "white",
          outline: "none",
          position: "absolute",
        }}
      >
        <button
          onClick={handleZoomIn}
          style={{
            color: "grey",
            fontSize: "16px",
            paddingTop: "5px",
            marginTop: "2px",
            border: "none",
            backgroundColor: "transparent",
            width: "20px",
            textAlign: "center",
            height: "100%",
            outline: "none",
          }}
        >
          +
        </button>
        <div style={{ width: "3px", height: "0.5px", backgroundColor: "white", margin: "10px 0" }}></div>
        <button
          onClick={handleZoomOut}
          style={{
            color: "grey",
            fontSize: "16px",
            paddingBottom: "5px",
            border: "none",
            backgroundColor: "transparent",
            width: "20px",
            height: "auto",
            textAlign: "center",
            marginBottom: "0px",
            outline: "none",
          }}
        >
          -
        </button>
      </div>
    );
  };

  // Memoized markers to prevent re-rendering
  const markers = useMemo(
    () =>
      (filterApplied ? filteredTrees : trees).map((tree) => (
        <Marker
          key={tree.properties.tid}
          position={[tree.geometry.coordinates[1], tree.geometry.coordinates[0]]}
          icon={getIconBySurveyType(tree.properties.survey_type)}
          eventHandlers={{ click: () => handleMarkerClick(tree) }}
          surveyType={tree.properties.survey_type}
        />
      )),
    [filterApplied, filteredTrees, trees, handleMarkerClick]
  );

  return (
    <div style={{ position: "relative", height: "100%", width: "100%" }}>
      {loading && <Loading />}
      <MapContainer
        center={position}
        zoom={zoom}
        style={{ width: "100%", height: "100vh" }}
        maxBounds={indiaBounds}
        maxBoundsViscosity={1.0}
        zoomControl={false}
        ref={mapRef}
        whenCreated={(map) => (mapRef.current = map)}
      >
        <div
          style={{
            position: "absolute",
            top: "2px",
            zIndex: 1000,
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            width: "100%",
            justifyContent: "space-between",
          }}
        >
          <img
            src={Logo2}
            alt="App Logo"
            className="logo"
            style={{ width: "300px", height: "100px", paddingBottom: "10px", marginTop: "5px", marginLeft: "10px" }}
          />
          <div className="top-right-controls" style={{ right: "10px", top: "10px", paddingLeft: "0px" }}>
            {isLoggedIn ? (
              <div className="user-account-container">
                <button onClick={() => navigate("/dashboard")} className="Dashboard-icon-btn">
                  <DashboardIcon style={{ width: "35px", height: "35px" }} />
                </button>
                <button onClick={toggleAccount} className="user-icon-btn">
                  <UserAccountIcon style={{ width: "35px", height: "35px", marginLeft: "10px" }} />
                </button>
                {isAccountOpen && <UserAccount user={user} onLogout={handleLogout} onClose={() => setIsAccountOpen(false)} />}
              </div>
            ) : (
              <button onClick={() => navigate("/login")} className="dashlog">
                Sign In
              </button>
            )}
          </div>
        </div>

        <Legend />
        {selectedLayer === "Street" && (
          <TileLayer
            url="https://www.google.cn/maps/vt?lyrs=m@189&gl=cn&x={x}&y={y}&z={z}"
            maxZoom={22}
            subdomains={["mt0", "mt1", "mt2", "mt3"]}
            noWrap={true}
          />
        )}
        {selectedLayer === "Satellite" && (
          <TileLayer
            url="http://mt0.google.com/vt/lyrs=y&hl=en&x={x}&y={y}&z={z}&s=Ga"
            maxZoom={22}
            subdomains={["mt0", "mt1", "mt2", "mt3"]}
            noWrap={true}
          />
        )}
        {selectedLayer === "OpenStreetMap" && <TileLayer url="https://{s}.tile.openstreetmap.fr/osmfr/{z}/{x}/{y}.png" maxZoom={22} />}

        <div className="button-container">
          
          <button onClick={handleLayerToggle} style={activeButtonStyle("layer")} aria-label="Toggle Layer Dropdown" title="Toggle Layer Dropdown">
            {isDropdownOpen ? <LayerIcon2 /> : <LayerIcon />}
          </button>
          {isDropdownOpen && (
            <LayerDropdown onLayerChange={handleLayerChange} selectedLayer={selectedLayer} onClose={() => setDropdownOpen(false)} />
          )}
          <button onClick={handleFlyToCurrentLocation} style={activeButtonStyle("currentLoc")} aria-label="Go to Current Location" title="Go to Current Location">
            <RulerIcon />
          </button>
          <button onClick={handleFilterToggle} style={activeButtonStyle("filter")} aria-label="Toggle Filter Panel" title="Toggle Filter Panel">
            {showFilterPanel ? <FilterIcon2 /> : <FilterIcon />}
          </button>
          {showFilterPanel && (
            <div ref={filterPanelRef}>
              <FilterPanel
                data={filterApplied ? filteredTrees : trees}
                onApplyFilters={handleApplyFilters}
                onResetFilters={handleResetFilters}
                focusOnLocation={focusOnLocation}
              />
            </div>
          )}
          <ZoomControl />
        </div>
{/* 
        <MarkerClusterGroup iconCreateFunction={createCustomClusterIcon} chunkedLoading>
          {markers}
        </MarkerClusterGroup> */}

          <MarkerClusterGroup
            iconCreateFunction={createCustomClusterIcon}
            chunkedLoading
            disableClusteringAtZoom={21}
            spiderfyOnMaxZoom={true}
            zoomToBoundsOnClick={true}
            showCoverageOnHover={false}
            maxClusterRadius={40}
          >
            {markers}
          </MarkerClusterGroup>

        {selectedTree && (
          <TreeInfoModal
            key={selectedTree?.properties?.tid}
            tree={selectedTree}
            visible={isModalVisible}
            onClose={handleModalClose}
            currentLocation={currentLocation}
          />
        )}
      </MapContainer>
    </div>
  );
};

export default MapComponent;